import {
  MAP_MOBILE_TIME_SLIDER_BOTTOM_PX,
  MAP_OVERLAY_Z,
} from '@/features/map/constants/mapOverlayLayout';
import { useMapOverlayInsets } from '@/features/map/hooks/useMapOverlayInsets';
import { useTimeSliderPlayback } from '@/features/map/hooks/useTimeSliderPlayback';
import { getFirstSelectableFloodTimelineDayIsoForYear } from '@/features/map/utils/floodTimelineCalendar';
import { useTemporalDisplayContext } from '@/features/scenario/hooks/useTemporalDisplayContext';
import {
  setSelectedDate,
  setTimeSliderHistoryDay,
  setTimeSliderPlaying,
} from '@/features/scenario/stores/scenarioSlice';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';

import { TimeSlider } from './TimeSlider';

/**
 * Single bottom time strip shared by 2D and 3D map tabs (Redux-backed).
 */
export function MapTimeSliderOverlay() {
  const dispatch = useAppDispatch();
  const { isMobile, contentInsetLeft, contentInsetRight } = useMapOverlayInsets();
  const { allowsHistoryTimeline } = useTemporalDisplayContext();
  const selectedDate = useAppSelector((s) => s.scenario.selectedDate);
  const selectedYear = useAppSelector((s) => s.scenario.selectedYear);
  const geeYear = useAppSelector((s) => s.map.geeParams.year);
  const isPlaying = useAppSelector((s) => s.scenario.timeSliderPlaying);
  const isAnyTemporalLayerVisible = useAppSelector((s) => {
    return s.layer.layers.some(
      (l) =>
        l.visible && (l.id === 'flood' || l.id.includes('forecast') || l.id.includes('simulation')),
    );
  });

  const playbackYear = selectedYear ?? geeYear;

  useTimeSliderPlayback(allowsHistoryTimeline);

  const resetToDayStart = () => {
    dispatch(setTimeSliderPlaying(false));
    const day = getFirstSelectableFloodTimelineDayIsoForYear(playbackYear);
    dispatch(setTimeSliderHistoryDay(day));
    dispatch(setSelectedDate(`${day}T00:00:00`));
  };

  if (!isAnyTemporalLayerVisible) {
    return null;
  }

  if (!allowsHistoryTimeline) {
    return null;
  }

  if (isMobile) {
    return (
      <div
        style={{
          position: 'absolute',
          left: contentInsetLeft,
          right: contentInsetRight,
          bottom: `calc(${MAP_MOBILE_TIME_SLIDER_BOTTOM_PX}px + env(safe-area-inset-bottom, 0px))`,
          display: 'flex',
          flexDirection: 'column-reverse',
          gap: 10,
          zIndex: MAP_OVERLAY_Z.tooltip,
          pointerEvents: 'auto',
        }}
      >
        <TimeSlider
          layout="stacked"
          year={playbackYear}
          currentDate={selectedDate}
          onChange={(date) => dispatch(setSelectedDate(date))}
          isPlaying={isPlaying}
          onTogglePlay={() => dispatch(setTimeSliderPlaying(!isPlaying))}
          onReset={resetToDayStart}
        />
      </div>
    );
  }

  return (
    <TimeSlider
      layout="floating"
      year={playbackYear}
      currentDate={selectedDate}
      onChange={(date) => dispatch(setSelectedDate(date))}
      isPlaying={isPlaying}
      onTogglePlay={() => dispatch(setTimeSliderPlaying(!isPlaying))}
      onReset={resetToDayStart}
    />
  );
}
